import { CUSTOM_TAB_TYPE } from '../../../types/common.type';
import { QUESTIONS_TEMPLATE_TYPE } from '../../../types/question.type';
import { HOSPITAL_CARDS_TYPE } from '../../../types/hospital.type';
import { CO_TRANSFER_CARDS_TYPE } from '../../../types/community-transfer.type';
import {
	CombineTotalPreviewBody,
	DaysPreviewBody,
	DcerPreviewBody,
	DxPreviewBody,
	FloorsPreviewBody,
	HospitalizationsPreviewBody,
	InsurancePreviewBody,
	NinetyDaysPreviewBody,
	ReturnsPreviewBody,
	ShiftPreviewBody,
	UserListPreviewBody,
} from './previewBodies';

/**
 * Pick the right preview body for a `dynamic` card on any page, using the
 * same branching `DynamicDataCardList` uses at runtime. This keeps the
 * layout-mode preview visually faithful — if a user renames / reconfigures
 * a custom question, its placeholder automatically shows the correct
 * silhouette without per-page wiring.
 *
 * `questionLike` is the card's `data` field (aka `ele` inside
 * `DynamicCardItem`). It may be missing in edge cases (a freshly added
 * card whose question data hasn't loaded yet) — we fall back to the
 * generic list preview in that case.
 *
 * Kept in sync with `features/.../DynamicDataCardList.js`.
 */
const resolveDynamicPreviewBody = (questionLike) => {
	if (!questionLike) return DxPreviewBody;

	const {
		type: questionType,
		templateType,
		isCustomDateAnalysis,
		customAnalysisType,
	} = questionLike;

	// --- Combine tab: one of 3 UI variants ---
	if (questionType === CUSTOM_TAB_TYPE.combineTab) {
		if (isCustomDateAnalysis && customAnalysisType === 'date') return NinetyDaysPreviewBody;
		if (isCustomDateAnalysis && customAnalysisType === 'time') return ShiftPreviewBody;
		return CombineTotalPreviewBody;
	}

	// --- Normal custom tab: keyed by templateType ---
	switch (templateType) {
		case QUESTIONS_TEMPLATE_TYPE.DAY_CARD:
		case HOSPITAL_CARDS_TYPE.DAYS_DATA:
			return DaysPreviewBody;

		case QUESTIONS_TEMPLATE_TYPE.USER_LIST:
		case HOSPITAL_CARDS_TYPE.DOCTOR_DATA:
		case HOSPITAL_CARDS_TYPE.NURSE_DATA:
			return UserListPreviewBody;

		case QUESTIONS_TEMPLATE_TYPE.FLOOR_CARD:
		case HOSPITAL_CARDS_TYPE.FLOORS_DATA:
			return FloorsPreviewBody;

		case QUESTIONS_TEMPLATE_TYPE.ANALYSIS:
		case HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA:
		case CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA:
			return NinetyDaysPreviewBody;

		case HOSPITAL_CARDS_TYPE.SHIFT_DATA:
			return ShiftPreviewBody;

		case HOSPITAL_CARDS_TYPE.INSURANCE_DATA:
			return InsurancePreviewBody;

		case HOSPITAL_CARDS_TYPE.DCER_DATA:
			return DcerPreviewBody;

		case HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS:
			return HospitalizationsPreviewBody;

		case HOSPITAL_CARDS_TYPE.RETURNS_DATA:
			return ReturnsPreviewBody;

		// DX_CARD and anything else falls through to the list template —
		// `DynamicDataCardList` itself does this via `default: DxCardTemplate`.
		default:
			return DxPreviewBody;
	}
};

export default resolveDynamicPreviewBody;
